import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ExternalLink, RefreshCw, FileText } from "lucide-react";

const dollars = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

export default function FamilyInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/families/me/invoices");
      setInvoices(data.results);
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const statusColor = {
    paid: "bg-sage-100 text-sage-800",
    open: "bg-ochre-100 text-ochre-800",
    void: "bg-red-100 text-red-700",
    draft: "bg-stone2-100 text-stone2-700",
  };

  return (
    <div className="min-h-screen bg-stone2-50" data-testid="family-invoices-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <Link to="/family" className="inline-flex items-center gap-1.5 text-sm text-sage-700 font-semibold">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>
        <div className="mt-2 flex items-start justify-between gap-3 flex-wrap">
          <h1 className="text-3xl font-heading font-bold text-sage-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-sage-600" /> Invoices
          </h1>
          <button type="button" onClick={load} className="text-xs text-sage-700 font-semibold inline-flex items-center gap-1 hover:text-sage-900">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
        <p className="mt-1 text-sm text-stone2-600">
          All invoices sent to you by sitters or by AutismBabysitters. Click "Pay" to open the secure Stripe payment page.
        </p>

        <div className="mt-8 paper-card overflow-hidden">
          {invoices.length === 0 ? (
            <div className="p-10 text-center text-stone2-500 text-sm">
              No invoices yet.
            </div>
          ) : (
            <ul className="divide-y divide-stone2-100" data-testid="family-invoices-list">
              {invoices.map((inv) => (
                <li key={inv.id} className="p-5 flex items-start justify-between gap-3 flex-wrap" data-testid={`family-invoice-row-${inv.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-semibold text-sage-900">
                        Invoice {inv.number || "—"}
                      </span>
                      <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${statusColor[inv.status] || "bg-stone2-100"}`}>
                        {inv.status}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-stone2-600">
                      From: <strong>{inv.sitter_name || "AutismBabysitters"}</strong>
                    </div>
                    {inv.memo && <div className="mt-1 text-xs text-stone2-500 italic">"{inv.memo}"</div>}
                    <div className="mt-2 text-xs text-stone2-500">
                      Sent {new Date(inv.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <div className="text-2xl font-heading font-bold text-sage-900">
                      {dollars(inv.amount_due_cents)}
                    </div>
                    {inv.status === "open" && inv.hosted_invoice_url && (
                      <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer"
                        data-testid={`family-invoice-pay-${inv.id}`}
                        className="btn-primary text-sm">
                        Pay now <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {inv.status === "paid" && (
                      <span className="text-sm font-semibold text-sage-700">✓ Paid</span>
                    )}
                    {inv.hosted_invoice_url && inv.status !== "open" && (
                      <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer"
                        className="text-xs text-sage-700 hover:text-sage-900 font-semibold inline-flex items-center gap-1">
                        View <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
